import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Box, Typography, Container, CircularProgress, Alert, Button,
  Card, CardContent, Grid, Tabs, Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import BatchResultsTable, { StatusBadge, ScoreChange } from '../components/BatchResultsTable';
import { fetchBatchIndex, fetchComplianceBundleUrl } from '../utils/batchResultsService';
import { Authority } from '../utilities/constants';
import CustomCredentialsProvider from '../utilities/CustomCredentialsProvider';

function SummaryCard({ value, label, color, variant }) {
  const bgMap = {
    accent: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
    error: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
    pending: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
  };
  const colorMap = {
    accent: '#059669',
    error: '#dc2626',
    pending: '#64748b',
  };
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        background: variant ? bgMap[variant] : '#fff',
        textAlign: 'center',
        transition: 'transform 0.15s',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color || (variant ? colorMap[variant] : '#1e40af'), lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

const columns = [
  {
    id: 'department', label: 'Department', sortable: true,
    render: (val) => (
      <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
        {val || '-'}
      </Typography>
    ),
  },
  {
    id: 'pipeline', label: 'Pipeline', sortable: true,
    render: (val) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{val || '-'}</Typography>,
  },
  {
    id: 'scan_tier_name', label: 'Tier', sortable: true,
    render: (val) => <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{val || '-'}</Typography>,
  },
  {
    id: 'source_path', label: 'Source Path', sortable: true,
    render: (val) => (
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b', wordBreak: 'break-all' }}>
        {val || '-'}
      </Typography>
    ),
  },
  {
    id: 'status', label: 'Status', sortable: true, align: 'center',
    render: (val) => <StatusBadge status={val} />,
  },
  { id: 'total_files', label: 'Total', sortable: true, align: 'center' },
  {
    id: 'completed', label: 'Done', sortable: true, align: 'center',
    render: (val) => <Typography variant="body2" sx={{ color: '#059669', fontWeight: 500 }}>{val}</Typography>,
  },
  {
    id: 'failed', label: 'Failed', sortable: true, align: 'center',
    render: (val) => (
      <Typography variant="body2" sx={{ color: val > 0 ? '#dc2626' : '#64748b', fontWeight: val > 0 ? 500 : 400 }}>
        {val}
      </Typography>
    ),
  },
  {
    id: 'avg_before', label: 'Avg Before', sortable: true, align: 'center',
    render: (val) => <Typography variant="body2">{val != null ? `${val}%` : '-'}</Typography>,
  },
  {
    id: 'avg_after', label: 'Avg After', sortable: true, align: 'center',
    render: (val) => <Typography variant="body2" sx={{ fontWeight: val != null ? 600 : 400 }}>{val != null ? `${val}%` : '-'}</Typography>,
  },
  {
    id: 'avg_improvement', label: 'Improvement', sortable: true, align: 'center',
    render: (val, row) => <ScoreChange before={row.avg_before} after={row.avg_after} />,
  },
  {
    id: 'success_rate', label: 'Success', sortable: true, align: 'center',
    render: (val) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{val}%</Typography>,
  },
  {
    id: 'estimated_cost', label: 'Est. Cost', sortable: true, align: 'center',
    render: (val) => <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>{val != null && val > 0 ? `$${val.toFixed(2)}` : '-'}</Typography>,
  },
  {
    id: 'created_at', label: 'Created', sortable: true,
    render: (val) => {
      if (!val) return '-';
      try {
        return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch { return val; }
    },
  },
];

export default function BatchResultsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [costs, setCosts] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [awsCredentials, setAwsCredentials] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(0); // 0 = "All"

  // Get AWS credentials
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    (async () => {
      try {
        const provider = new CustomCredentialsProvider();
        provider.loadFederatedLogin({ domain: Authority, token: auth.user?.id_token });
        const { credentials: c } = await provider.getCredentialsAndIdentityId();
        setAwsCredentials({
          accessKeyId: c.accessKeyId,
          secretAccessKey: c.secretAccessKey,
          sessionToken: c.sessionToken,
        });
      } catch (err) {
        console.error('Error fetching credentials:', err);
        setError('Failed to obtain AWS credentials. Please try refreshing.');
      }
    })();
  }, [auth.isAuthenticated, auth.user]);

  const loadData = useCallback(async () => {
    if (!awsCredentials) return;
    setLoading(true);
    setError(null);
    try {
      const { batches: data, costs: costData, generated_at } = await fetchBatchIndex(awsCredentials);
      const rows = data.map(b => ({
        ...b,
        _key: b.batch_id,
        success_rate: b.total_files > 0 ? Math.round((b.completed / b.total_files) * 100 * 10) / 10 : 0,
      }));
      setBatches(rows);
      setCosts(costData);
      setGeneratedAt(generated_at);
    } catch (err) {
      console.error('Error loading batch index:', err);
      setError('Failed to load batch results. The data may not have been exported yet. Contact the CITD Innovation Team.');
    } finally {
      setLoading(false);
    }
  }, [awsCredentials]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Compute batch groups for tabs
  const batchGroups = useMemo(() => {
    const groups = [...new Set(batches.map(b => b.batch_group || 1))].sort((a, b) => a - b);
    return groups;
  }, [batches]);

  // Filter by selected group
  const filteredBatches = useMemo(() => {
    if (selectedGroup === 0) return batches;
    return batches.filter(b => (b.batch_group || 1) === selectedGroup);
  }, [batches, selectedGroup]);

  const totalFiles = filteredBatches.reduce((s, b) => s + (b.total_files || 0), 0);
  const totalCompleted = filteredBatches.reduce((s, b) => s + (b.completed || 0), 0);
  const totalFailed = filteredBatches.reduce((s, b) => s + (b.failed || 0), 0);
  const pendingCount = filteredBatches.filter(b => b.status === 'PENDING').length;
  const improvementVals = filteredBatches.filter(b => b.avg_improvement != null).map(b => b.avg_improvement);
  const avgImprovement = improvementVals.length > 0
    ? Math.round((improvementVals.reduce((s, v) => s + v, 0) / improvementVals.length) * 10) / 10
    : null;

  // Make department clickable to go to batch detail
  const columnsWithLink = [
    {
      ...columns[0],
      render: (val, row) => {
        if (row.status === 'PENDING') {
          return <Typography variant="body2" sx={{ textTransform: 'capitalize', color: '#94a3b8' }}>{val || '-'}</Typography>;
        }
        return (
          <Typography
            variant="body2"
            sx={{
              textTransform: 'capitalize', fontWeight: 500, color: '#3b82f6', cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => navigate(`/batch-results/${row.batch_id}`)}
          >
            {val || '-'}
          </Typography>
        );
      },
    },
    ...columns.slice(1),
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #3b82f6 100%)',
        color: '#fff', py: 4, px: 3,
      }}>
        <Container maxWidth="xl">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app')}
            sx={{ color: '#fff', opacity: 0.8, mb: 1, '&:hover': { opacity: 1 } }}
          >
            Back to App
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Department Batch Remediation
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, mt: 0.5 }}>
            Miami-Dade County CITD - Accessibility Compliance
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -2, position: 'relative', zIndex: 1, pb: 6 }}>
        {/* Batch Group Tabs */}
        {batchGroups.length > 1 && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Tabs
              value={selectedGroup}
              onChange={(_, v) => setSelectedGroup(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 40 },
                '& .Mui-selected': { color: '#1e40af' },
              }}
            >
              <Tab label="All Groups" value={0} />
              {batchGroups.map(g => (
                <Tab key={g} label={`Batch ${g}`} value={g} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard value={filteredBatches.length} label="Total Batches" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard value={totalFiles} label="Total PDF files" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <SummaryCard value={totalCompleted} label="Files Completed" variant="accent" />
          </Grid>
          {totalFailed > 0 && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SummaryCard value={totalFailed} label="Files Failed" variant="error" />
            </Grid>
          )}
          {pendingCount > 0 && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SummaryCard value={pendingCount} label="Batches Pending" variant="pending" />
            </Grid>
          )}
          {avgImprovement != null && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SummaryCard value={`+${avgImprovement}%`} label="Avg Score Improvement" variant="accent" />
            </Grid>
          )}
          {costs && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SummaryCard value={`$${costs.total_aws_cost}`} label="Total AWS Cost" />
            </Grid>
          )}
          {costs && costs.cost_per_file > 0 && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SummaryCard value={`$${costs.cost_per_file}`} label="Avg Cost / File" />
            </Grid>
          )}
        </Grid>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                const url = await fetchComplianceBundleUrl(awsCredentials);
                if (url) {
                  window.open(url, '_blank');
                } else {
                  setError('No compliance report available yet. Run Export-ComplianceReport.ps1 first.');
                }
              } catch {
                setError('No compliance report available yet. Run Export-ComplianceReport.ps1 first.');
              }
            }}
            disabled={!awsCredentials || loading}
            sx={{
              borderColor: '#1e40af',
              color: '#1e40af',
              fontWeight: 600,
              '&:hover': { borderColor: '#1e3a8a', backgroundColor: '#eff6ff' },
            }}
          >
            Download Compliance Report
          </Button>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : (
          <BatchResultsTable
            columns={columnsWithLink}
            rows={filteredBatches}
            defaultSort="created_at"
            defaultOrder="desc"
            defaultComparator={(a, b) => {
              const sp = { PROCESSING: 0, UPLOADING: 1, PARTIAL: 2, COMPLETED: 3, FAILED: 4, PENDING: 5 };
              const sa = sp[a.status] ?? 9;
              const sb = sp[b.status] ?? 9;
              if (sa !== sb) return sa - sb;
              const da = a.created_at || '';
              const db = b.created_at || '';
              if (db > da) return 1;
              if (db < da) return -1;
              return 0;
            }}
            searchPlaceholder="Filter by department, status, or pipeline..."
          />
        )}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          ADA PDF Remediation Tool &middot; Miami-Dade County CITD
          {generatedAt && (
            <Box sx={{ mt: 0.5 }}>
              Data generated: {new Date(generatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
